import {LitElement, html, TemplateResult, PropertyValues} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import {getFormJSON} from './forms';
/**
 * `inventory-planner`
 */
export class InventoryPlanner extends LitElement {
  /**
   * Render in the light dom
   *
   * @returns {object} this
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Defined the elements content
   *
   * @returns {TemplateResult} the resulting html template
   */
  render() {
    const header = html`<h3>Inventory Planner</h3>`;
    if (this.isError) {
      return [header, html`<p>Oops there has been an error loading the inventory planner integration</p>`];
    }

    if (this.isLoading) {
      return [header, html`<p>Loading inventory planner integration...</p>`];
    }

    return [header, html`
    <p>To get the following information:</p>
    <ul>
      <li>Log in to <a href="https://www.inventory-planner.com/" target="_blank">Inventory Planner</a> using web browser
      </li>
      <li>Go to Account, Settings, API tab</li>
      <li>Use <b>Generate key</b> to generate API key</li>
    </ul>
    <form name="inventoryPlanner" @submit=${this._submit}>
      <label class="checkbox">
        Do you want to sync purchase orders from Inventory Planner to the Warehouse?<br>
        And sync received items back to Inventory Planner from your Warehouse?
        <input type="checkbox" name="isEnabled" ?checked=${ifDefined(this.integration?.isEnabled)}>
      </label>
      <label>
        Account ID
        <input name="accountID" required placeholder="Example: abc123" .value=${ifDefined(this.integration?.accountID)}>
      </label>
      <label>
        API Key
        <input name="apiKey" type="password" ?required=${this.integration?.accountID?.length === 0} placeholder="Example: XXXXXXXXX">
        <small>We will never display your api key here for improved security, if you want to set or update it please
          fill
          out this value. Otherwise it will remain as the api key you've previously set and you may leave this field
          blank.</small>
          <br>
      </label>
      <label class="checkbox">
        Do you want to only sync purchase orders from Inventory Planner to the Warehouse?
        <input type="checkbox" name="isPurchaseOrdersOnly" ?checked=${ifDefined(this.integration?.isPurchaseOrdersOnly)}>
      </label>
      <div class="buttons">
        <small>This will mean transfer orders and assembly orders will not be synced to the Warehouse.</small>
      </div>
      <div class="buttons">
        <button type="submit">Save</button>
        <button type="reset">Clear</button>
      </div>
      <div class="status">
        <p ?hidden=${!this.isSavingComplete}>Inventory planner integration saved.</p>
        <p ?hidden=${!this.isSavingError}>Error saving your inventory planner integration.</p>
        <p ?hidden=${!this.isSaving}>Saving inventory planner integration...</p>
      </div>
    </form>`];
  }

  /**
   * Defines the elements properties
   *
   * @returns {object} the elements properties
   */
  static get properties() {
    return {
      /** Used to interact with api */
      service: {type: Object},
      /** The integration information */
      integration: {type: Object},
      /** If we are loading data */
      isLoading: {type: Boolean},
      /** If there is an error loading data*/
      isError: {type: Boolean},
      /** If we are saving data */
      isSaving: {type: Boolean},
      /** If there is an error saving data*/
      isSavingError: {type: Boolean},
      /** If we have saved data */
      isSavingComplete: {type: Boolean},
    };
  }

  /** Initialises values of properties */
  constructor() {
    super();
    this.service = {};
    this.isLoading = false;
    this.isError = false;
    this.isSaving = false;
    this.isSavingError = false;
    this.isSavingComplete = false;
    this.integration = {};
  }

  /**
   * Fired whenever any property changes and the template updates
   *
   * @param {PropertyValues} changedProperties map of changed properties
   */
  updated(changedProperties) {
    if (super.updated) {
      super.updated(changedProperties);
    }

    if (changedProperties.has('service') &&
      this.service?.getIntegration
    ) {
      this._load();
    }
  }

  /**
   * Load the integration info
   */
  async _load() {
    this.isError = false;
    this.isLoading = true;
    try {
      const integration = await this.service.getIntegration('inventoryPlanner');
      this.integration = integration;
    } catch (e) {
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Save the integration info
   *
   * @param {object} event the form event
   */
  async _submit(event) {
    event.preventDefault();
    this.isSaving = false;
    this.isSavingError = false;
    const form = this.querySelector('form[name="inventoryPlanner"]');
    const valid = form.reportValidity();
    if (valid) {
      try {
        this.isSaving = true;
        const data = getFormJSON(form);
        await this.service.upsertIntegration('inventoryPlanner', {
          ...data,
          isEnabled: !!data.isEnabled,
          isPurchaseOrdersOnly: !!data.isPurchaseOrdersOnly,
        });
        this.isSaving = false;
        this.isSavingComplete = true;
      } catch (e) {
        this.isSaving = false;
        this.isSavingError = true;
      } finally {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        this.isSaving = false;
        this.isSavingError = false;
        this.isSavingComplete = false;
      }
    }
  }
}
window.customElements.define('inventory-planner', InventoryPlanner);
